import { usePageAnalyticDetailContext } from '../../contexts/PageAnalyticDetailProvider';
import { TARGET_CHANNEL, TARGET_DEVICES, TARGET_VISITOR } from '~/constants/campaign';
import { buildBreakdownDonutData, PACardDonutChart } from '../../modules/analytic';
import { EReportSource } from '~/modules/report-analytic/types';
import { useNavigateToReport } from '~/modules/report-analytic';
import { usePageAnalyticFilterStore } from '../../stores';
import { IPAMetricKey } from '~/modules/analytics/types';
import { EAnalyticColumnKey } from '~/core/gemxql/types';
import { LOCATION_PATH_KEY } from '../../constants';
import { useSearchParams } from '@remix-run/react';
import { useCallback, useMemo } from 'react';
import { Grid } from '@shopify/polaris';

const ANALYTICS_DATA = [
  { key: IPAMetricKey.PAGE_SESSIONS_BY_VISITOR, title: 'Visitors by type' },
  { key: IPAMetricKey.PAGE_SESSIONS_BY_DEVICE, title: 'Sessions by device type' },
  { key: IPAMetricKey.PAGE_SESSIONS_BY_TRAFFIC, title: 'Sessions by traffic source' },
];

export const PageAnalyticGridDonut = () => {
  const { totalsRow, comparisonTotalsRow, isLoading, isEmptyMetricData } =
    usePageAnalyticDetailContext();
  const analyticMode = usePageAnalyticFilterStore((state) => state.analyticMode);

  const [searchParams] = useSearchParams();
  const locationPath = searchParams.get(LOCATION_PATH_KEY);

  const { handleNavigateToReport } = useNavigateToReport();

  const { visitorData, deviceData, trafficData } = useMemo(
    () => ({
      visitorData: buildBreakdownDonutData({
        targets: TARGET_VISITOR,
        columnKey: EAnalyticColumnKey.VISITOR_ITEMS,
        totalsRow,
        comparisonTotalsRow,
      }),
      deviceData: buildBreakdownDonutData({
        targets: TARGET_DEVICES,
        columnKey: EAnalyticColumnKey.DEVICE_ITEMS,
        totalsRow,
        comparisonTotalsRow,
      }),
      trafficData: buildBreakdownDonutData({
        targets: TARGET_CHANNEL,
        columnKey: EAnalyticColumnKey.TRAFFIC_SOURCE_ITEMS,
        totalsRow,
        comparisonTotalsRow,
        sort: true,
      }),
    }),
    [totalsRow, comparisonTotalsRow],
  );

  const getDataByKey = useCallback(
    (key: IPAMetricKey) => {
      switch (key) {
        case IPAMetricKey.PAGE_SESSIONS_BY_VISITOR:
          return visitorData;
        case IPAMetricKey.PAGE_SESSIONS_BY_DEVICE:
          return deviceData;
        case IPAMetricKey.PAGE_SESSIONS_BY_TRAFFIC:
          return trafficData;
        default:
          return [];
      }
    },
    [deviceData, trafficData, visitorData],
  );

  return (
    <Grid gap={{ xs: '200' }}>
      {ANALYTICS_DATA.map(({ key, title }) => (
        <Grid.Cell
          key={key}
          columnSpan={{
            xs: 6,
            lg: 4,
          }}
        >
          <PACardDonutChart
            label={title}
            onClick={() =>
              handleNavigateToReport(key, {
                locationPath,
                mode: analyticMode,
                from: EReportSource.PAGE,
              })
            }
            tooltipKey={key}
            data={getDataByKey(key)}
            isLoading={isLoading}
            isEmptyMetricData={isEmptyMetricData}
          />
        </Grid.Cell>
      ))}
    </Grid>
  );
};
